/**
 * PlanJobBanner — Global floating indicator when plan generation is running.
 *
 * Visible across all pages. Two lines: action label + playful Nico quote (Ich-Perspektive).
 * Tap opens the full progress view in PlanGeneratorSheet.
 */

import { useState, useEffect, useRef } from 'react';
import { usePlanJob } from '../hooks/usePlanJob';
import { openPlanSheet } from './FloraActionHub';

// ── Step action labels (line 1) ──

const STEP_ACTIONS: Record<number, string> = {
  0: "Planerstellung wird gestartet",
  1: "Suche & wähle Rezepte",
  2: "Erstelle neue Rezepte",
  3: "Prüfe Nährwerte",
  4: "Stelle den Plan zusammen",
  5: "Erstelle Tipps & Zusammenfassung",
};

// ── Playful Nico quotes per step (Ich-Perspektive, line 2) ──

const STEP_QUOTES: Record<number, string[]> = {
  0: [
    "Moment, ich such meine Kochschürze...",
    "Ich wärm schon mal die Pfanne vor!",
    "Bin gleich soweit, versprochen!",
  ],
  1: [
    "Ich stöber mal durch die Rezepte...",
    "Hmm, das passt gut von den Kalorien!",
    "Ich such was Leckeres für dich raus!",
    "Dein Profil sieht gut aus, ich such mal los!",
    "Mal schauen was in der Datenbank steckt...",
    "Ich achte drauf, dass die Kalorien stimmen!",
  ],
  2: [
    "Ich rühr gerade im Topf... mit Liebe!",
    "Kreativität braucht eben seine Zeit!",
    "Ich schnippel gerade Gemüse, gleich fertig!",
    "Das wird richtig lecker, wart's ab!",
    "Ich probier was Neues aus... vertrau mir!",
    "Langsam gekocht schmeckt besser!",
    "Meine besten Rezepte brauchen etwas Geduld.",
    "Ich geb mir extra Mühe bei den Rezepten!",
  ],
  3: [
    "Kurzer Check... sieht gut aus!",
    "Kalorien stimmen, Protein auch!",
    "Alles im grünen Bereich!",
  ],
  4: [
    "Gleich geschafft, ich puzzel die letzten Teile!",
    "Fast fertig! Ich sortier nur noch...",
    "Der Plan nimmt Form an!",
    "Endspurt! Bin gleich durch!",
  ],
  5: [
    "Noch ein paar Tipps und dann wars das!",
    "Ich fass nochmal alles zusammen...",
    "Die Ziellinie ist in Sicht!",
    "Letzte Handgriffe, versprochen!",
  ],
};

function getRandomQuote(step: number): string {
  const quotes = STEP_QUOTES[step] || STEP_QUOTES[0];
  return quotes[Math.floor(Math.random() * quotes.length)];
}

export default function PlanJobBanner() {
  const job = usePlanJob();
  const [quote, setQuote] = useState('');
  const [visible, setVisible] = useState(false);
  const lastStep = useRef(-1);

  // Pick new quote when step changes
  useEffect(() => {
    if (job.isRunning && job.currentStep !== lastStep.current) {
      lastStep.current = job.currentStep;
      setQuote(getRandomQuote(job.currentStep));
    }
  }, [job.isRunning, job.currentStep]);

  // Rotate quote every 8s within the same step
  useEffect(() => {
    if (!job.isRunning) return;
    const interval = setInterval(() => {
      setQuote(getRandomQuote(job.currentStep));
    }, 8000);
    return () => clearInterval(interval);
  }, [job.isRunning, job.currentStep]);

  // Show/hide
  useEffect(() => {
    if (job.isRunning) {
      setVisible(true);
      if (!quote) setQuote(getRandomQuote(job.currentStep));
    } else {
      const t = setTimeout(() => setVisible(false), 600);
      return () => clearTimeout(t);
    }
  }, [job.isRunning]);

  if (!visible) return null;

  const action = STEP_ACTIONS[job.currentStep] || STEP_ACTIONS[0];

  return (
    <div
      onClick={openPlanSheet}
      className="fixed top-0 left-0 right-0 z-[70] cursor-pointer"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      <div className="mx-3 mt-2 md:mx-auto md:max-w-md">
        <div className="bg-emerald-600 text-white rounded-2xl px-4 py-3.5 shadow-lg shadow-emerald-600/20 flex items-center gap-3.5 active:scale-[0.98] transition-transform">
          {/* Nico avatar — large */}
          <div className="w-14 h-14 shrink-0">
            <img
              src="/nico-transparent.svg"
              alt="Nico"
              className="w-full h-full nico-breathe"
            />
          </div>

          {/* Two-line text + progress */}
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold leading-tight">{action}</p>
            <p className="text-[12px] text-white/70 leading-tight mt-0.5 italic truncate">{quote}</p>
            <div className="flex items-center gap-2 mt-1.5">
              <div className="flex-1 bg-white/20 rounded-full h-1.5">
                <div
                  className="bg-white h-1.5 rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${job.pct}%` }}
                />
              </div>
              <span className="text-[10px] text-white/60 tabular-nums shrink-0">{job.currentStep}/5</span>
            </div>
          </div>

          {/* Chevron */}
          <svg className="w-4 h-4 text-white/30 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </div>
  );
}
